<template>
    <div class="card bl-page">
        <PageHeader
            title="Master Budget Line Setup"
            subtitle="Create and manage master budget lines"
            btnTitle="Add Master Budget Line"
            :showBtn="false"
            :showAddNew="true"
            @onClickAddNewButton="onClickAddNew"
        />

        <div class="filter-section">
            <div class="filter-bar">
                <div class="filter-dates">
                    <div class="filter-date-field">
                        <label class="filter-label">From</label>
                        <input v-model="start" type="date" class="form-control filter-date-input">
                    </div>
                    <div class="filter-date-field">
                        <label class="filter-label">To</label>
                        <input v-model="end" type="date" class="form-control filter-date-input">
                    </div>
                    <button type="button" class="btn btn-primary filter-go-btn" :disabled="loading" @click="onClickGo">Go</button>
                </div>

                <div class="filter-bar-actions">
                    <span class="result-summary">{{ resultSummary }}</span>
                </div>
            </div>
        </div>

        <div class="table-section">
            <BudgetLineTable
                :rows="budgetLines"
                :loading="loading"
                @edit="onClickEdit"
                @add="onClickAddNew"
            />
        </div>

        <div class="pagination-section">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import Pagination from '@/components/atom/Pagination'
import BudgetLineTable from '@/components/molecule/procurement/budget-line-setup/BudgetLineTable.vue'
import handleProjects from '@/services/modules/procurement/project'
import useDate from '@/services/utils/day'
import { getApiErrorMessage } from '@/services/utils/global.js'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const { fetchProjectList } = handleProjects()
const { currentDate, formatForInput, getFirstDateOfMonth } = useDate()

const offset = ref(20)
const loading = ref(false)
const budgetLines = ref([])
const pageMeta = ref({ from: 0, to: 0, total: 0 })
const start = ref('')
const end = ref('')

const companyId = computed(() => $route.params.companyId)

const resultSummary = computed(() => {
    if (loading.value) return 'Loading…'
    if (! pageMeta.value.total) return 'No records'
    return `Showing ${pageMeta.value.from}–${pageMeta.value.to} of ${pageMeta.value.total}`
})

const getQuery = (page) => {
    let query = '?company_id=' + companyId.value
    query += '&is_company_budget=1'
    query += '&offset=' + offset.value
    if (start.value && end.value) query += `&start_date=${start.value}&end_date=${end.value}`
    if (page) query += '&page=' + page
    return query
}

const setPagination = (data) => {
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const clearList = () => {
    budgetLines.value = []
    pageMeta.value = { from: 0, to: 0, total: 0 }
    $store.commit('resetPagination')
}

const applyResponse = (res) => {
    if (! res.status) return clearList()
    budgetLines.value = res.data.data
    pageMeta.value = { from: res.data.from, to: res.data.to, total: res.data.total }
    setPagination(res.data)
}

const getBudgetLines = async (page) => {
    loading.value = true
    try {
        const res = await fetchProjectList(getQuery(page))
        applyResponse(res)
    } catch (err) {
        clearList()
        showError(getApiErrorMessage(err))
    } finally {
        loading.value = false
    }
}

const buildDateQuery = () => {
    const routeQuery = Object.assign({}, $route.query, { page: 1 })
    delete routeQuery.start
    delete routeQuery.end
    if (start.value) routeQuery.start = start.value
    if (end.value) routeQuery.end = end.value
    return routeQuery
}

const onClickGo = async () => {
    await $router.push({ path: $route.path, query: buildDateQuery() })
    await getBudgetLines(1)
}

const onPageChange = async (page) => {
    await $router.push({ path: $route.path, query: Object.assign({}, $route.query, { page }) })
    await getBudgetLines(page)
}

const onClickAddNew = () => {
    $router.push({ name: 'budget-line-setup-add', params: $route.params, query: $route.query })
}

const onClickEdit = (budgetLineId) => {
    $router.push({
        name: 'budget-line-setup-edit',
        params: Object.assign({}, $route.params, { budgetLineId }),
        query: $route.query
    })
}

const initFilters = () => {
    start.value = $route.query.start || formatForInput(getFirstDateOfMonth())
    end.value = $route.query.end || currentDate()
}

onMounted(async () => {
    initFilters()
    await getBudgetLines($route.query.page)
})
</script>

<style scoped>
.bl-page {
    min-height: calc(100vh - 158px);
}
.filter-section {
    padding: 18px 20px 0;
}
.filter-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-dates {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-date-field {
    display: flex;
    flex-direction: column;
}
.filter-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #344054;
}
.filter-date-input {
    width: 180px;
    height: 40px;
    border-radius: 8px;
    border-color: #e4e7ec;
    font-size: 14px;
}
.filter-go-btn {
    height: 40px;
    min-width: 76px;
    border-radius: 8px;
    font-size: 14px;
}
.filter-bar-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}
.result-summary {
    font-size: 14px;
    color: #667085;
    white-space: nowrap;
}
.table-section {
    padding: 16px 20px 0;
}
.pagination-section {
    padding: 14px 20px 18px;
}
@media (max-width: 576px) {
    .filter-section {
        padding: 14px 16px 0;
    }
    .table-section {
        padding: 14px 16px 0;
    }
    .pagination-section {
        padding: 12px 16px 16px;
    }
    .filter-dates {
        width: 100%;
    }
    .filter-date-field {
        flex: 1 1 140px;
    }
    .filter-date-input {
        width: 100%;
    }
    .filter-bar-actions {
        width: 100%;
        justify-content: space-between;
        margin-left: 0;
    }
}
</style>
